import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MoreVertical, Send, Check, CheckCheck, Ban, Flag, Trash2, UserRound, Clock, ShieldOff, MapPin } from "lucide-react";
import { useAppData } from "../context/AppDataContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { EmptyState, Spinner } from "../components/common";
import AudioPlayer from "../components/chat/AudioPlayer";
import AudioRecorder from "../components/chat/AudioRecorder";
import { ReportUserDialog, ConfirmDialog } from "../components/chat/Dialogs";
import { Avatar } from "./MessagesPage";
import { api, apiError, fileUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { chatTime } from "../lib/chat";
import { useUnread } from "../context/UnreadContext";

function Bubble({ m }) {
  const { lang } = useLang();
  return (
    <div className={`flex ${m.mine ? "justify-end" : "justify-start"} fade-up`} data-testid={`message-${m.id}`}>
      <div className={`max-w-[80%] rounded-[20px] px-3.5 py-2 shadow-sm ${m.mine ? "rounded-br-md bg-brand text-white" : "rounded-bl-md bg-[#EEF2F7] text-navy"}`}>
        {m.type === "audio" ? <AudioPlayer src={m.audio_url} duration={m.duration} mine={m.mine} testid={`audio-${m.id}`} />
          : <p className="whitespace-pre-wrap break-words text-[15px] leading-snug" data-testid="message-text">{m.text}</p>}
        <div className={`mt-0.5 flex items-center justify-end gap-1 text-[10.5px] ${m.mine ? "text-white/80" : "text-slate-400"}`}>
          {chatTime(m.created_at, lang)}
          {m.mine && (m.read ? <CheckCheck className="h-3.5 w-3.5 text-white" data-testid="message-read" /> : <Check className="h-3.5 w-3.5" data-testid="message-sent" />)}
        </div>
      </div>
    </div>
  );
}

function Header({ conv, onMenu }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const name = conv.other.deleted ? t("msg.deletedUser") : conv.other.name;
  return (
    <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/95 pt-safe backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-2">
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/mensajes"))} className="rounded-full p-2 text-navy hover:bg-mist" aria-label="back" data-testid="chat-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <button onClick={() => conv.other.user_id && navigate(`/usuario/${conv.other.user_id}`)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left" data-testid="chat-other-btn">
          <Avatar user={conv.other} size="h-9 w-9" />
          <span className="truncate font-extrabold text-navy" data-testid="chat-other-name">{name}</span>
        </button>
        {onMenu}
      </div>
      <button onClick={() => navigate(`/anuncio/${conv.listing.id}`)} data-testid="chat-listing-header"
        className="mx-auto flex w-full max-w-3xl items-center gap-3 border-t border-slate-100 px-4 py-2 text-left transition-colors hover:bg-mist">
        {conv.listing.photo && <img src={fileUrl(conv.listing.photo)} alt="" className="h-10 w-10 rounded-lg object-cover" />}
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-navy">{conv.listing.title}</span>
        <span className="text-xs font-semibold text-brand">›</span>
      </button>
    </header>
  );
}

function RequestPanel({ conv, onAction }) {
  const { t, lang } = useLang();
  const { zoneName } = useAppData();
  const p = conv.requester_profile;
  const a = p?.attributes || {};
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[0_6px_24px_rgba(13,42,107,0.1)]" data-testid="request-panel">
      <div className="p-5 pb-3">
        <p className="text-xs font-bold uppercase tracking-wider text-alert">{t("msg.requestIntro")}</p>
        <p className="mt-1 text-lg font-extrabold text-navy">{t("msg.wantsToContact", { name: conv.other.name })}</p>
        <p className="mt-1 text-sm text-slate-500">{t("msg.requestIntroSub")}</p>
      </div>
      {p && (
        <div data-testid="request-profile">
          <div className="no-scrollbar flex snap-x snap-mandatory gap-2 overflow-x-auto px-5 touch-pan-x" data-no-swipe>
            {p.photos.map((ph) => (
              <img key={ph} src={fileUrl(ph)} alt="" className="aspect-[3/4] w-40 shrink-0 snap-start rounded-2xl object-cover" />
            ))}
          </div>
          <div className="px-5 pt-3">
            <p className="text-xl font-extrabold text-navy" data-testid="request-profile-title">{p.title}</p>
            <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5 text-alert" />{zoneName(p.zone)}</p>
            {a.looking_for?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="request-profile-busca">
                {a.looking_for.map((v) => <span key={v} className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand">{t(`opt.looking_for.${v}`)}</span>)}
              </div>
            )}
            {p.description && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-navy/80" lang={lang}>{p.description}</p>}
          </div>
        </div>
      )}
      <div className="p-5 pt-4">
        <button className="btn-primary w-full py-3.5" onClick={() => onAction("accept")} data-testid="request-accept-btn">{t("msg.accept")}</button>
        <div className="mt-3 flex justify-center gap-5 text-sm font-semibold">
          <button className="text-slate-500 hover:text-navy" onClick={() => onAction("reject")} data-testid="request-reject-btn">{t("msg.reject")}</button>
          <button className="text-alert" onClick={() => onAction("block")} data-testid="request-block-btn">{t("msg.block")}</button>
          <button className="text-alert" onClick={() => onAction("report")} data-testid="request-report-btn">{t("msg.report")}</button>
        </div>
      </div>
    </div>
  );
}

function Composer({ conv, onSent, autoAudio }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const send = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/messages`, payload);
      await onSent();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail === "request_pending" ? t("msg.requestSent") : apiError(e, t));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (text.trim() && (await send({ type: "text", text: text.trim() }))) setText("");
  };
  return (
    <form onSubmit={submit} className="mx-auto flex max-w-3xl items-end gap-2 px-3 py-2.5" data-testid="chat-composer">
      {!recording && (
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 2000))} rows={1} placeholder={t("msg.placeholder")}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          className="field max-h-32 min-h-[44px] flex-1 resize-none rounded-[22px] py-2.5" data-testid="chat-text-input" />
      )}
      {text.trim() && !recording ? (
        <button type="submit" disabled={busy} aria-label={t("msg.send")} data-testid="chat-send-btn"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-md transition-transform active:scale-90 disabled:opacity-50">
          <Send className="h-5 w-5" />
        </button>
      ) : (
        <AudioRecorder disabled={busy} autoFocus={autoAudio} onActiveChange={setRecording}
          onSend={({ path, duration }) => send({ type: "audio", audio_path: path, duration })} />
      )}
    </form>
  );
}

export default function ChatPage() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useUnread();
  const [conv, setConv] = useState(null);
  const [error, setError] = useState(false);
  const [report, setReport] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const endRef = useRef(null);
  const count = conv?.messages.length;

  const load = useCallback(() => api.get(`/conversations/${id}`).then((r) => setConv(r.data)).catch(() => setError(true)), [id]);
  useEffect(() => {
    load().then(refresh);
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [count]);

  const act = async (action) => {
    if (action === "report") return setReport(true);
    if (action === "block") return setConfirm("block");
    if (action === "delete") return setConfirm("delete");
    try {
      if (action === "accept") setConv((await api.post(`/conversations/${id}/accept`)).data);
      if (action === "reject") { await api.post(`/conversations/${id}/reject`); toast(t("msg.rejected")); refresh(); navigate("/mensajes", { replace: true }); }
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const doConfirm = async () => {
    try {
      if (confirm === "block") { await api.post(`/conversations/${id}/block`); toast.success(t("msg.blocked")); load(); }
      if (confirm === "delete") { await api.delete(`/conversations/${id}`); toast(t("msg.deleted")); navigate("/mensajes", { replace: true }); }
      refresh();
    } catch (e) { toast.error(apiError(e, t)); }
  };

  if (error) return <div className="min-h-[100dvh] bg-mist pt-20"><EmptyState icon={ShieldOff} title={t("detail.notFound")} testid="chat-not-found" /></div>;
  if (!conv) return <div className="flex min-h-[100dvh] items-center justify-center"><Spinner /></div>;

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full p-2 text-navy hover:bg-mist" aria-label={t("msg.more")} data-testid="chat-menu-btn">
        <MoreVertical className="h-5 w-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl">
        {!conv.other.deleted && <DropdownMenuItem onClick={() => navigate(`/usuario/${conv.other.user_id}`)} data-testid="chat-menu-profile"><UserRound className="mr-2 h-4 w-4" />{t("msg.viewProfile")}</DropdownMenuItem>}
        {!conv.blocked_by_me && !conv.other.deleted && <DropdownMenuItem onClick={() => act("block")} data-testid="chat-menu-block"><Ban className="mr-2 h-4 w-4 text-alert" />{t("msg.block")}</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => act("report")} data-testid="chat-menu-report"><Flag className="mr-2 h-4 w-4 text-alert" />{t("msg.report")}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => act("delete")} data-testid="chat-menu-delete"><Trash2 className="mr-2 h-4 w-4" />{t("msg.delete")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex h-[100dvh] flex-col bg-mist" data-testid="chat-page">
      <Header conv={conv} onMenu={menu} />
      <main className="flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto max-w-3xl space-y-2 px-3 py-4" data-testid="chat-messages">
          {conv.request_sent && (
            <div className="mx-auto mt-4 flex max-w-sm items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-navy shadow-sm" data-testid="chat-request-sent">
              <Clock className="h-4 w-4 shrink-0 text-brand" />
              <span>{t("contact.pending")}<span className="block text-xs font-normal text-slate-500">{t("msg.requestSentSub")}</span></span>
            </div>
          )}
          {conv.messages.map((m) => <Bubble key={m.id} m={m} />)}
          {conv.kind === "request" && conv.status === "open" && conv.messages.length === 0 && (
            <p className="mx-auto max-w-sm rounded-2xl bg-white px-4 py-3 text-center text-sm text-slate-500" data-testid="chat-accepted-hint">{t("contact.acceptedHint")}</p>
          )}
          {conv.awaiting_my_answer && <div className="pt-3"><RequestPanel conv={conv} onAction={act} /></div>}
          <div ref={endRef} />
        </div>
      </main>
      <footer className="border-t border-slate-100 bg-white pb-safe">
        {conv.blocked_by_me ? <p className="px-4 py-4 text-center text-sm text-slate-500" data-testid="chat-blocked-banner">{t("msg.blockedBanner")}</p>
          : conv.awaiting_my_answer || conv.request_sent ? null
          : conv.can_send ? <Composer conv={conv} onSent={load} autoAudio={location.state?.audio} />
          : <p className="px-4 py-4 text-center text-sm text-slate-500" data-testid="chat-cant-send">{t("msg.cantSend")}</p>}
      </footer>
      <ReportUserDialog open={report} onOpenChange={setReport} conversationId={id} />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} testid={`confirm-${confirm || "x"}`}
        title={confirm === "block" ? t("msg.blockConfirm", { name: conv.other.name }) : t("msg.deleteConfirm")}
        sub={confirm === "block" ? t("msg.blockConfirmSub") : t("msg.deleteConfirmSub")}
        confirmLabel={confirm === "block" ? t("msg.block") : t("msg.delete")} onConfirm={doConfirm} />
    </div>
  );
}
